import { AlertIcon } from '#assets/ReactSvg.ts';
import { Button } from '#components/atoms/buttons/Button.tsx';
import { Link } from '#components/atoms/link/Link.tsx';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '#components/patterns/modal/index.ts';
import { Spacer } from '#components/patterns/spacer/Spacer.tsx';
import type { APIKeyListItem } from '#dn-types/api-key.ts';
import type { EndpointOIDC } from '#dn-types/endpoint-oidc.ts';
import type { Host } from '#dn-types/host.ts';
import type { JoinKeyListItem } from '#dn-types/join-key.ts';
import type { Role } from '#dn-types/role.ts';
import styles from './DeleteRoleError.module.css';

type Props = {
  onClose: () => void;
  role: Role;
  firstFiveHostsWithRole: Host[];
  totalHostsWithRole: number;
  endpointOIDCProviders: EndpointOIDC[];
  apiKeys: APIKeyListItem[];
  joinKeys: JoinKeyListItem[];
};

/**
 * {@link DeleteRoleError} is a modal that warns the user that they have hosts, endpoint SSO providers,
 * API keys or join keys that depend on the role, and prevents them from deleting the role.
 */
export function DeleteRoleError({
  onClose,
  role,
  firstFiveHostsWithRole,
  totalHostsWithRole,
  endpointOIDCProviders,
  apiKeys,
  joinKeys,
}: Props) {
  const remainingHostsLength = totalHostsWithRole - firstFiveHostsWithRole.length;

  return (
    <Modal isOpen={true} onClose={onClose}>
      <ModalHeader>
        <div className={styles.DeleteRoleError_title}>
          <AlertIcon className="mr-4" />
          <div className="truncate mw-0">Cannot delete role {role.name}</div>
        </div>
      </ModalHeader>
      <ModalBody>
        {firstFiveHostsWithRole.length > 0 && (
          <>
            <p className="p">
              You cannot delete {role.name} because it is currently assigned to the following host
              {firstFiveHostsWithRole.length === 1 ? '' : 's'}:
            </p>
            <div className="p">
              <ul>
                {firstFiveHostsWithRole.map((host) => (
                  <li key={host.id}>
                    <Link className="a" to={`/hosts/${host.id}`}>
                      {host.name}
                    </Link>
                  </li>
                ))}
              </ul>
              {remainingHostsLength > 0 && (
                <Link className="a" to={`/hosts?filter.roleID=${role.id}`}>
                  and {remainingHostsLength} more host{remainingHostsLength === 1 ? '' : 's'}
                </Link>
              )}{' '}
            </div>
          </>
        )}
        {endpointOIDCProviders.length > 0 && (
          <>
            <p className="p">
              {role.name} is used by the following endpoint SSO provider
              {endpointOIDCProviders.length === 1 ? '' : 's'}. Change{' '}
              {endpointOIDCProviders.length === 1 ? 'its' : 'their'} role first:
            </p>
            <ul className="p">
              {endpointOIDCProviders.map((provider) => (
                <li key={provider.id}>
                  <Link className="a" to={`/settings/sso/${provider.id}`}>
                    {provider.name}
                  </Link>
                </li>
              ))}
            </ul>
          </>
        )}
        {apiKeys.length > 0 && (
          <>
            <p className="p">
              {role.name} is the scope of the following API key{apiKeys.length === 1 ? '' : 's'}. Delete{' '}
              {apiKeys.length === 1 ? 'it' : 'them'} first:
            </p>
            <ul className="p">
              {apiKeys.map((apiKey) => (
                <li key={apiKey.id}>
                  <Link className="a" to={`/settings/api-keys/${apiKey.id}`}>
                    {apiKey.name}
                  </Link>
                </li>
              ))}
            </ul>
          </>
        )}
        {joinKeys.length > 0 && (
          <>
            <p className="p">
              {role.name} is given to hosts enrolled with the following join key{joinKeys.length === 1 ? '' : 's'}.
              Revoke {joinKeys.length === 1 ? 'it' : 'them'} first:
            </p>
            <ul className="p">
              {joinKeys.map((joinKey) => (
                <li key={joinKey.id}>
                  <Link className="a" to={`/settings/join-keys/${joinKey.id}`}>
                    {joinKey.name}
                  </Link>
                </li>
              ))}
            </ul>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Spacer>
          <Button onClick={onClose}>Close</Button>
        </Spacer>
      </ModalFooter>
    </Modal>
  );
}
